import { Button, Image, UIHeading1, UIHeading2, useDeviceType, useUserBrowserTheme } from '@cian/ui-kit';
import cn from 'clsx';
import React, { useCallback, useState } from 'react';

import { PageIndicator } from '../PageIndicator';
import { PROGRAM_GUIDE_PAGES } from './pages';
import styles from './styles.css';

export interface IProgramGuideModalContentProps {
  onClose: () => void;
}

export const ProgramGuideModalContent: React.FC<IProgramGuideModalContentProps> = ({ onClose }) => {
  const theme = useUserBrowserTheme();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [isPageTransitionEnabled, setIsPageTransitionEnabled] = useState(false);
  const isLastPage = activePageIndex === PROGRAM_GUIDE_PAGES.length - 1;
  const imageHeight = isDesktop ? 242 : 160;
  const MainHeading = isDesktop ? UIHeading1 : UIHeading2;

  const changePage = useCallback((getNextIndex: (prev: number) => number) => {
    setIsPageTransitionEnabled(true);
    setActivePageIndex(getNextIndex);
  }, []);

  const handleNextClick = useCallback(() => {
    if (isLastPage) {
      onClose();

      return;
    }

    changePage(prev => prev + 1);
  }, [changePage, isLastPage, onClose]);

  const handleBackClick = useCallback(() => {
    changePage(prev => Math.max(0, prev - 1));
  }, [changePage]);

  return (
    <div className={cn(styles.container, styles[deviceType])} data-testid="program-guide-modal">
      <div className={cn(styles.content, isPageTransitionEnabled && styles['pages-animated'])}>
        <MainHeading fontWeight="bold">Как выбрать программу</MainHeading>

        <div className={styles['media-stack']}>
          {PROGRAM_GUIDE_PAGES.map((page, pageIndex) => {
            const imageUrl = theme === 'dark' ? page.image.dark : page.image.light;
            const isActive = pageIndex === activePageIndex;

            return (
              <div
                key={`${page.title}-media`}
                className={cn(styles['media-layer'], isActive && styles['media-layer-active'])}
                aria-hidden={!isActive}
              >
                {imageUrl ? (
                  <div className={styles.image}>
                    <Image
                      src={imageUrl}
                      alt={page.title}
                      width="100%"
                      height={imageHeight}
                      borderRadius={'16px'}
                      objectFit="cover"
                      preloader
                    />
                  </div>
                ) : (
                  <div className={styles['image-placeholder']} aria-hidden />
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.pages}>
          {PROGRAM_GUIDE_PAGES.map((page, pageIndex) => {
            const isActive = pageIndex === activePageIndex;

            return (
              <div
                key={page.title}
                className={cn(styles.page, isActive && styles['page-active'])}
                aria-hidden={!isActive}
              >
                <div className={styles['page-title']}>
                  <UIHeading2 fontWeight="bold">{page.title}</UIHeading2>
                </div>

                <div className={styles.description}>{page.description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {isDesktop ? (
        <div className={styles['footer-desktop']}>
          <div className={styles['footer-side']}>
            {activePageIndex > 0 && (
              <Button
                theme="fill_secondary"
                size="L"
                onClick={handleBackClick}
                data-testid="program-guide-modal-back-button"
              >
                Назад
              </Button>
            )}
          </div>
          <PageIndicator count={PROGRAM_GUIDE_PAGES.length} activeIndex={activePageIndex} />
          <div className={styles['footer-side-right']}>
            <Button size="L" onClick={handleNextClick} data-testid="program-guide-modal-button">
              {isLastPage ? 'Закрыть' : 'Далее'}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.footer}>
          <PageIndicator count={PROGRAM_GUIDE_PAGES.length} activeIndex={activePageIndex} />
          <Button fullWidth size="L" onClick={handleNextClick} data-testid="program-guide-modal-button">
            {isLastPage ? 'Закрыть' : 'Далее'}
          </Button>
        </div>
      )}
    </div>
  );
};

ProgramGuideModalContent.displayName = 'ProgramGuideModalContent';
